import { Link } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { Button, Heading, Text } from "@nbs/ui";
import { downloadCsv } from "@/features/analytics/utils/download-csv";
import {
  formatMeasureStatusLabel,
  formatQualityLabel,
} from "@/features/analytics/utils/domain-labels";
import { formatNbsNumber } from "@/features/analytics/utils/format-number";
import { NbsChartContainer } from "@/features/charts/NbsChartContainer";
import { NbsHorizontalBarChart } from "@/features/charts/NbsHorizontalBarChart";
import { NbsKpiCard } from "@/features/charts/NbsKpiCard";
import { NbsLineChart } from "@/features/charts/NbsLineChart";
import {
  NbsCatalogFilterBar,
  type CatalogFilterField,
} from "@/features/filters/NbsCatalogFilterBar";
import { RoleContextBanner } from "@/features/rbac/RoleContextBanner";
import { usePermissions } from "@/hooks/usePermissions";
import { MEASURES_CATALOG_QUERY } from "../api/documents";
import { useMeasureFilters } from "../hooks/useMeasureFilters";
import { useMeasureLifecycle } from "../hooks/useMeasureLifecycle";

const PAGE_SIZE = 40;

export function MeasuresCatalog() {
  const permissions = usePermissions();
  const { state, setState, reset, graphqlFilters, activeCount } = useMeasureFilters();
  const { data, loading, error, refetch } = useQuery(MEASURES_CATALOG_QUERY, {
    variables: {
      filters: graphqlFilters,
      first: PAGE_SIZE,
      offset: state.page * PAGE_SIZE,
      sort: state.sort,
    },
    fetchPolicy: "cache-and-network",
  });
  const lifecycle = useMeasureLifecycle(() => void refetch());
  const canPublish = permissions.can("action.publishMeasure");
  const canScientificValidate = permissions.can("action.scientificValidate");

  const summary = data?.measureSummary;
  const options = data?.measureFilterOptions;
  const analytics = data?.measureAnalytics;
  const page = data?.measureCatalog;
  const nodes = page?.nodes ?? [];
  const totalPages = Math.max(1, Math.ceil((page?.totalCount ?? 0) / PAGE_SIZE));

  const pageState = error
    ? "error"
    : loading && !data
      ? "loading"
      : !summary
        ? "empty"
        : activeCount > 0 && nodes.length === 0
          ? "filtered-empty"
          : "populated";

  const years = (options?.years ?? [2005, 2015, 2025]) as number[];
  const filterFields: CatalogFilterField[] = [
    {
      kind: "search",
      key: "search",
      label: "Recherche",
      inputId: "mes-q",
      value: state.search,
      placeholder: "Indicateur, méthode, valeur…",
      onChange: (search) => setState({ search, page: 0 }),
    },
    {
      kind: "multi",
      key: "indicateurs",
      label: "Indicateur",
      selectId: "mes-ind",
      value: state.indicateurIds,
      options: (options?.indicateurs ?? []).map((i: { id: string; libelle: string }) => ({
        value: i.id,
        label: i.libelle,
      })),
      onChange: (indicateurIds) => setState({ indicateurIds, page: 0 }),
    },
    {
      kind: "multi",
      key: "services",
      label: "Service",
      selectId: "mes-svc",
      value: state.serviceIds,
      options: (options?.services ?? []).map((s: { id: string; libelle: string }) => ({
        value: s.id,
        label: s.libelle,
      })),
      onChange: (serviceIds) => setState({ serviceIds, page: 0 }),
    },
    {
      kind: "multi",
      key: "territories",
      label: "Territoire",
      selectId: "mes-terr",
      value: state.territoryIds,
      options: (options?.territories ?? []).map(
        (t: { id: string; label: string; kind: string }) => ({
          value: t.id,
          label: `${t.label} (${t.kind})`,
        }),
      ),
      onChange: (territoryIds) => setState({ territoryIds, page: 0 }),
    },
    {
      kind: "multi",
      key: "emprise",
      label: "Emprise",
      selectId: "mes-kind",
      value: state.empriseKinds,
      options: (options?.empriseKinds ?? []).map((k: string) => ({ value: k, label: k })),
      onChange: (empriseKinds) => setState({ empriseKinds, page: 0 }),
    },
    {
      kind: "multi",
      key: "qualites",
      label: "Qualité",
      selectId: "mes-qual",
      value: state.qualites,
      options: (options?.qualites ?? []).map((q: string) => ({
        value: q,
        label: formatQualityLabel(q),
      })),
      onChange: (qualites) => setState({ qualites, page: 0 }),
    },
    {
      kind: "multi",
      key: "statuses",
      label: "Statut",
      selectId: "mes-status",
      value: state.statuses,
      options: (options?.measureStatuses ?? []).map((s: string) => ({
        value: s,
        label: formatMeasureStatusLabel(s),
      })),
      onChange: (statuses) => setState({ statuses, page: 0 }),
    },
    {
      kind: "single",
      key: "fromYear",
      label: "De",
      selectId: "mes-from",
      value: state.fromYear,
      options: [
        { value: "", label: "—" },
        ...years.map((y) => ({ value: String(y), label: String(y) })),
      ],
      onChange: (fromYear) => setState({ fromYear, page: 0 }),
    },
    {
      kind: "single",
      key: "toYear",
      label: "À",
      selectId: "mes-to",
      value: state.toYear,
      options: [
        { value: "", label: "—" },
        ...years.map((y) => ({ value: String(y), label: String(y) })),
      ],
      onChange: (toYear) => setState({ toYear, page: 0 }),
    },
    {
      kind: "single",
      key: "sort",
      label: "Tri",
      selectId: "mes-sort",
      value: state.sort,
      options: [
        { value: "period_desc", label: "Période ↓" },
        { value: "period_asc", label: "Période ↑" },
        { value: "value_desc", label: "Valeur ↓" },
        { value: "value_asc", label: "Valeur ↑" },
        { value: "indicateur_asc", label: "Indicateur A→Z" },
      ],
      onChange: (sort) => setState({ sort, page: 0 }),
    },
  ];

  return (
    <article
      className="nbs-catalog"
      data-testid="measures-catalog"
      data-state={pageState}
      data-role={permissions.productLabel}
    >
      <header className="nbs-catalog__header">
        <div>
          <Heading level={1}>Mesures</Heading>
          <Text tone="muted">
            {permissions.can("data.nonPublishedMeasures")
              ? "Registre des observations — y compris brouillons et files de revue selon votre profil."
              : "Registre des observations publiées uniquement. Point d’entrée vers le détail N3 et la provenance."}
          </Text>
        </div>
        {permissions.can("export.published") ? (
          <Button
            type="button"
            variant="secondary"
            onClick={() =>
              downloadCsv("mesures.csv", [
                [
                  "id",
                  "valeur",
                  "unite",
                  "indicateur",
                  "service",
                  "territoire",
                  "debut",
                  "fin",
                  "methode",
                  "qualite",
                  "statut",
                  "source",
                  "page",
                ],
                ...nodes.map(
                  (n: {
                    id: string;
                    value: number;
                    unit: string;
                    indicateurLibelle: string;
                    serviceLibelle: string;
                    territoryLabel: string;
                    periodStart: string;
                    periodEnd: string;
                    method: string;
                    quality: string;
                    status: string;
                    source: string | null;
                    page: number | null;
                  }) => [
                    n.id,
                    formatNbsNumber(n.value, { empty: "" }),
                    n.unit,
                    n.indicateurLibelle,
                    n.serviceLibelle,
                    n.territoryLabel,
                    n.periodStart,
                    n.periodEnd,
                    n.method,
                    n.quality,
                    n.status,
                    n.source ?? "",
                    n.page != null ? String(n.page) : "",
                  ],
                ),
              ])
            }
          >
            Exporter CSV
          </Button>
        ) : null}
      </header>

      <RoleContextBanner />

      <div className="nbs-kpi-grid" aria-label="KPI mesures">
        <NbsKpiCard label="Total des mesures" value={summary?.totalCount} />
        <NbsKpiCard label="Mesures validées" value={summary?.publishedCount} />
        {permissions.can("data.nonPublishedMeasures") ? (
          <NbsKpiCard label="À revoir" value={summary?.toReviewCount} />
        ) : null}
        <NbsKpiCard label="Indicateurs mesurés" value={summary?.indicatorCount} />
        <NbsKpiCard label="Territoires couverts" value={summary?.territoryCount} />
        <NbsKpiCard
          label="Dernière période"
          value={summary?.periodMax?.slice(0, 4)}
          period={
            summary?.periodMin && summary?.periodMax
              ? `${summary.periodMin.slice(0, 4)}–${summary.periodMax.slice(0, 4)}`
              : null
          }
        />
        {permissions.can("data.nonPublishedMeasures") ? (
          <NbsKpiCard label="Anomalies qualité" value={summary?.anomalyCount} />
        ) : null}
      </div>

      <NbsCatalogFilterBar
        ariaLabel="Filtres mesures"
        fields={filterFields}
        activeCount={activeCount}
        onReset={reset}
      />

      {error ? (
        <Text role="alert" className="nbs-text--error">
          {error.message}{" "}
          <button type="button" className="nbs-link-button" onClick={() => void refetch()}>
            Réessayer
          </button>
        </Text>
      ) : null}

      {lifecycle.error ? (
        <Text role="alert" className="nbs-text--error">
          {lifecycle.error}{" "}
          <button type="button" className="nbs-link-button" onClick={lifecycle.clearError}>
            Fermer
          </button>
        </Text>
      ) : null}

      <div className="nbs-n1-analytics__split">
        <NbsChartContainer
          title="Mesures par service"
          empty={!loading && !analytics?.byService?.length}
          isLoading={loading && !analytics}
        >
          <NbsHorizontalBarChart
            data={(analytics?.byService ?? []).map((b: { label: string; value: number }) => ({
              label: b.label,
              value: b.value,
            }))}
            unit=""
            height={240}
          />
        </NbsChartContainer>
        <NbsChartContainer
          title="Mesures par territoire"
          empty={!loading && !analytics?.byTerritory?.length}
          isLoading={loading && !analytics}
        >
          <NbsHorizontalBarChart
            data={(analytics?.byTerritory ?? []).map((b: { label: string; value: number }) => ({
              label: b.label,
              value: b.value,
            }))}
            unit=""
            height={240}
            color="#2f5d8c"
          />
        </NbsChartContainer>
      </div>

      <NbsChartContainer
        title="Chronologie des mesures"
        empty={!loading && !analytics?.byYear?.length}
        isLoading={loading && !analytics}
      >
        <NbsLineChart
          data={(analytics?.byYear ?? []).map((b: { label: string; value: number }) => ({
            year: b.label,
            mesures: b.value,
          }))}
          xKey="year"
          seriesKeys={["mesures"]}
          height={220}
        />
      </NbsChartContainer>

      {pageState === "filtered-empty" ? (
        <div className="nbs-catalog__empty">
          <Text>Aucune mesure pour ces filtres.</Text>
          <Button type="button" variant="secondary" onClick={reset}>
            Réinitialiser les filtres
          </Button>
        </div>
      ) : null}

      <div className="nbs-catalog-table-wrap">
        <table className="nbs-catalog-table">
          <thead>
            <tr>
              <th>Valeur</th>
              <th>Unité</th>
              <th>Indicateur</th>
              <th>Service</th>
              <th>Territoire</th>
              <th>Dimensions</th>
              <th>Début</th>
              <th>Fin</th>
              <th>Méthode</th>
              <th>Qualité</th>
              <th>Statut</th>
              <th>Source</th>
              <th>Page</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {nodes.map(
              (n: {
                id: string;
                value: number;
                unit: string;
                indicateurId: string;
                indicateurLibelle: string;
                serviceLibelle: string;
                territoryLabel: string;
                dimensions: Array<{ label: string }>;
                periodStart: string;
                periodEnd: string;
                method: string;
                quality: string;
                status: string;
                source: string | null;
                page: number | null;
              }) => (
                <tr key={n.id}>
                  <td>{formatNbsNumber(n.value)}</td>
                  <td>{n.unit}</td>
                  <td>
                    <Link to={`/n2?indicateurId=${n.indicateurId}`}>{n.indicateurLibelle}</Link>
                  </td>
                  <td>{n.serviceLibelle}</td>
                  <td>{n.territoryLabel}</td>
                  <td className="nbs-catalog-table__dims">
                    {n.dimensions.map((d) => d.label).join(", ") || "—"}
                  </td>
                  <td>{n.periodStart.slice(0, 10)}</td>
                  <td>{n.periodEnd.slice(0, 10)}</td>
                  <td title={n.method}>
                    {n.method.length > 40 ? `${n.method.slice(0, 40)}…` : n.method}
                  </td>
                  <td>{formatQualityLabel(n.quality)}</td>
                  <td>{formatMeasureStatusLabel(n.status)}</td>
                  <td>{n.source ?? "—"}</td>
                  <td>{n.page ?? "—"}</td>
                  <td className="nbs-catalog-table__actions">
                    <Link to={`/n3?mesureIds=${n.id}`}>Voir le détail</Link>
                    {canScientificValidate && n.status === "controlee" ? (
                      <button
                        type="button"
                        className="nbs-link-button"
                        disabled={lifecycle.busyId === n.id}
                        onClick={() => void lifecycle.scientificValidate(n.id, n.status)}
                      >
                        {lifecycle.busyId === n.id ? "Validation…" : "Valider scientifiquement"}
                      </button>
                    ) : null}
                    {canPublish && n.status === "validee" ? (
                      <button
                        type="button"
                        className="nbs-link-button"
                        disabled={lifecycle.busyId === n.id}
                        onClick={() => void lifecycle.publish(n.id, n.status)}
                      >
                        {lifecycle.busyId === n.id ? "Publication…" : "Publier"}
                      </button>
                    ) : null}
                  </td>
                </tr>
              ),
            )}
          </tbody>
        </table>
      </div>

      <footer className="nbs-catalog__pager">
        <Text tone="muted">
          {page?.totalCount ?? 0} mesure(s) · page {state.page + 1}/{totalPages}
        </Text>
        <div className="nbs-catalog__pager-actions">
          <Button
            type="button"
            variant="secondary"
            disabled={state.page <= 0}
            onClick={() => setState({ page: state.page - 1 })}
          >
            Précédent
          </Button>
          <Button
            type="button"
            variant="secondary"
            disabled={state.page + 1 >= totalPages}
            onClick={() => setState({ page: state.page + 1 })}
          >
            Suivant
          </Button>
        </div>
      </footer>
    </article>
  );
}
